import { RouteParams } from '../../routing';

/**
 * Parses the current URL pathname based on the provided route template.
 * @function useRouteParser
 * @template T - The type of the route template.
 * @param {T} route - The route template to parse.
 * @returns {RouteParams<T>} An object containing the parsed route parameters.
 * @throws {Error} If the function is used outside of the browser or if the pathname does not match the route.
 */
export const useRouteParser = <T extends string>(route: T): RouteParams<T> => {
  if (typeof window === 'undefined')
    throw new Error('useRouteParser can only be used in the browser.');

  const pathname = window.location.pathname;

  const routeParts = route
    .split('/')
    .filter((x) => x)
    .map((x) => x.toLowerCase());
  const pathParts = pathname
    .split('/')
    .filter((x) => x)
    .map((x) => x.toLowerCase());

  const firstParamIndex = routeParts.findIndex((x) => x.startsWith(':'));

  if (firstParamIndex < 0) return {} as RouteParams<T>;

  const firstOptionalIndex = routeParts.findIndex((x) => x.endsWith('?'));

  const satisifesRequiredLength = pathParts.length >= firstOptionalIndex;

  const requiredExplicits = routeParts
    .slice(0, firstOptionalIndex)
    .filter((x) => !x.startsWith(':'));

  const satisifesRequiredExplicits = requiredExplicits.every((x) =>
    pathParts.includes(x),
  );

  const optionalTrailingSlash = '(?:/|$)';

  const normalizedRoute = `/${routeParts.slice(firstParamIndex).join('/')}`;
  const normalizedPath = `/${pathParts.slice(firstParamIndex).join('/')}`;

  const regex = normalizedRoute
    .replace(/\?/g, '') // strip '?'
    .replace(/\/(?!:)[a-zA-Z0-9_]+\/?/g, `.*?${optionalTrailingSlash}`) // explicit params
    .replace(/:([a-zA-Z0-9_]+)\/?/g, `(.*?)${optionalTrailingSlash}`) // capture params
    .replace(/\//g, '\\/'); // escape slashes

  const match = normalizedPath.match(`^${regex}${optionalTrailingSlash}$`);

  if (!satisifesRequiredLength || !satisifesRequiredExplicits || !match)
    throw new Error(`pathname '${pathname}' does not match route '${route}'`);

  const keys = Array.from(route.matchAll(/:([a-zA-Z0-9_]+)\??/g)).map(
    ([, key]) => key,
  );
  const values = match.slice(1);

  return keys.reduce<Record<string, string | undefined>>((acc, key, index) => {
    if (key) {
      acc[key] = values[index] ? values[index] : undefined;
    }
    return acc;
  }, {}) as RouteParams<T>;
};
